import React from 'react';
import { Helmet } from 'react-helmet-async';
import { VerificationDoc, ScannedTTR } from './types';

// Default OG image (1200x630) when the position has no image.
// TODO(v0): replace with the real hosted Schmidt default OG asset.
const DEFAULT_OG_IMAGE = '/og-default.png';

interface Props {
  doc: VerificationDoc;
  scanned: ScannedTTR | null;
  rawTtrCode: string | undefined;
}

/**
 * Per-scan SEO + OpenGraph/Twitter tags via react-helmet-async.
 *
 * LIMITATION (TD-30 — client-rendered SEO): react-helmet-async sets these tags
 * client-side after the bundle loads, so crawlers that don't execute JavaScript
 * (some link-preview bots) still see only the static index.html head. The durable
 * fix is a server-side prerender of /sku/** (a prerender Cloud Function, or moving
 * the public page to the golive Next.js app). Helmet makes the SSR migration path
 * cleaner if/when we prerender.
 */
const VerifyMeta: React.FC<Props> = ({ doc, scanned, rawTtrCode }) => {
  const name = doc.display.positionLabel || 'Registered Item';
  const board = doc.display.boardName || 'Collectors Guild';
  const title = `${name} · ${board} · TTR Verified | Schmidt Auctions`;

  const owner = doc.ownership.ownerName ? `Owned by ${doc.ownership.ownerName}. ` : '';
  const holder = doc.ownership.holderName || 'Schmidt Auctions';
  const description =
    `${name} from ${board} — verified in The Collectors Guild protocol. ` +
    `${owner}Held by ${holder}.`;

  const image = doc.display.imageUrl || doc.display.imageUrlObverse || DEFAULT_OG_IMAGE;
  const url =
    typeof window !== 'undefined'
      ? `${window.location.origin}/sku/${encodeURIComponent(scanned?.ttrCode || rawTtrCode || '')}`
      : '';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default VerifyMeta;
